import React, { useState } from 'react';
import { Users, Luggage, Wifi, Coffee, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const Fleet = () => {
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  
  const fleet = [
    {
      id: 1,
      model: "Mercedes Classe V",
      category: t('fleet.category'),
      passengers: "6-7",
      luggage: "8",
      features: t('fleet.features'),
      image: "https://customer-assets.emergentagent.com/job_rent-with-driver-to/artifacts/j3o7xvag_IMG_20260117_131615_355.PNG",
      gallery: [
        "https://customer-assets.emergentagent.com/job_rent-with-driver-to/artifacts/j3o7xvag_IMG_20260117_131615_355.PNG",
        "https://customer-assets.emergentagent.com/job_rent-with-driver-to/artifacts/fvziq3eo_IMG_20260117_131603_609.PNG",
        "https://customer-assets.emergentagent.com/job_rent-with-driver-to/artifacts/iufgz25z_IMG_20260117_131603_515.PNG"
      ],
      color: t('fleet.color')
    }
  ];

  const nextImage = (vehicleId, galleryLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [vehicleId]: ((prev[vehicleId] || 0) + 1) % galleryLength
    }));
  };

  const prevImage = (vehicleId, galleryLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [vehicleId]: ((prev[vehicleId] || 0) - 1 + galleryLength) % galleryLength
    }));
  };

  return (
    <section id="flotta" className="py-20 bg-gray-950 scroll-mt-20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block">
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-2 block">
              {t('fleet.badge')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('fleet.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">{t('fleet.titleHighlight')}</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto"></div>
          </div>
          <p className="text-gray-400 text-lg mt-6 max-w-2xl mx-auto">
            {t('fleet.subtitle')}
          </p>
        </div>

        {/* Fleet Grid */}
        <div className="grid lg:grid-cols-1 gap-8 max-w-4xl mx-auto">
          {fleet.map((vehicle, index) => {
            const currentIdx = currentImageIndex[vehicle.id] || 0;
            const displayImage = vehicle.gallery ? vehicle.gallery[currentIdx] : vehicle.image;
            const hasGallery = vehicle.gallery && vehicle.gallery.length > 1;
            
            return (
              <Card 
                key={vehicle.id}
                className="group bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800 hover:border-amber-500/50 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20"
              >
                {/* Vehicle Image with Gallery */}
                <div className="relative h-96 overflow-hidden">
                  <img 
                    src={displayImage}
                    alt={vehicle.model}
                    className="w-full h-full object-cover transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/30 to-transparent"></div>
                  
                  {/* Gallery Navigation */}
                  {hasGallery && (
                    <>
                      <Button
                        onClick={() => prevImage(vehicle.id, vehicle.gallery.length)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full w-10 h-10 p-0 transition-all duration-300"
                      >
                        <ChevronLeft className="w-6 h-6 text-white" />
                      </Button>
                      <Button
                        onClick={() => nextImage(vehicle.id, vehicle.gallery.length)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full w-10 h-10 p-0 transition-all duration-300"
                      >
                        <ChevronRight className="w-6 h-6 text-white" />
                      </Button>
                      {/* Gallery Dots */}
                      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex space-x-2">
                        {vehicle.gallery.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(prev => ({ ...prev, [vehicle.id]: idx }))}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                              idx === currentIdx ? 'bg-amber-400 w-8' : 'bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  
                  {/* Featured Badge */}
                  {index === 0 && (
                    <Badge className="absolute top-6 right-6 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-4 py-2 text-sm">
                      {t('fleet.vehicleBadge')}
                    </Badge>
                  )}
                </div>

              {/* Vehicle Details */}
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-3xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors duration-300">
                      {vehicle.model}
                    </h3>
                    <p className="text-gray-400 text-lg">{vehicle.category}</p>
                  </div>
                  <Badge variant="outline" className="border-amber-500/30 text-amber-400 bg-amber-500/10">
                    {vehicle.color}
                  </Badge>
                </div>

                {/* Capacity Info */}
                <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-800">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">{t('fleet.passengers')}</p>
                      <p className="text-white font-bold">{vehicle.passengers}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
                      <Luggage className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">{t('fleet.luggage')}</p>
                      <p className="text-white font-bold">{vehicle.luggage}</p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h4 className="text-white font-semibold mb-4 flex items-center">
                    <Coffee className="w-5 h-5 text-amber-400 mr-2" />
                    {t('fleet.comfortTitle')}
                  </h4>
                  <ul className="grid grid-cols-2 gap-3">
                    {vehicle.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-300 text-sm">
                        <Check className="w-4 h-4 text-amber-400 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
            );
          })}
        </div>

        {/* Additional Fleet Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-lg">
            {t('fleet.footer')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Fleet;
